import React, { useMemo, useCallback, useEffect, useContext } from 'react';
import useSelect from 'hooks/useSelect';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import DataRoomSelectedDocumentsContext from './DataRoomSelectedDocuments.context';
import { useLocation } from 'react-router-dom';
import DataRoomDocumentsContext from '../DataRoomDocuments/DataRoomDocuments.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { getIsNodeFolder, getNodeMimeType, isVideo } from 'utils';
import { isDataRoomViewer } from 'router/subrouters/DataRoom/utils';
import useDataRoomNodeAutorized from 'router/subrouters/DataRoom/hooks/useDataRoomNodeAutorized';
import useActionPerNodes from 'router/subrouters/DataRoom/hooks/useActionPerNodes';

const DataRoomDocumentsProvider: React.FC = ({ children }) => {
  const { currentlyDisplayedNodes, buySidePreviewMember } = useContext(
    DataRoomDocumentsContext,
  );

  const { role } = useContext(DataRoomContext);

  const isViewer = useMemo(() => isDataRoomViewer(role), [role]);

  const selectableNodes = useMemo(
    () => currentlyDisplayedNodes,
    [currentlyDisplayedNodes],
  );

  const {
    selected,
    isSingleSelected,
    isAllSelected,
    isSelectedMode,
    selectAll,
    setSelected,
    removeAll,
    updateSelected,
    onSelect,
    updateSelectedValue,
  } = useSelect<DataRoomNode>(selectableNodes);

  const { selectedLabel, canDownloadSelected, canShareFiles } =
    useActionPerNodes(selected);

  const { hasAuthorizedActionsForAll, shouldNotManage } =
    useDataRoomNodeAutorized(selected);

  const shouldRename = useMemo(
    () => isSingleSelected && hasAuthorizedActionsForAll,
    [hasAuthorizedActionsForAll, isSingleSelected],
  );

  const shouldAllowBulkActions = useMemo(
    () => !buySidePreviewMember,
    [buySidePreviewMember],
  );

  const isSingleSelectedVideo = useMemo(
    () => isSingleSelected && isVideo(getNodeMimeType(selected[0])),
    [isSingleSelected, selected],
  );

  const isSingleSelectedFolder = useMemo(
    () => isSingleSelected && getIsNodeFolder(selected[0]),
    [isSingleSelected, selected],
  );

  const { pathname } = useLocation();

  const selectAllNodes = useCallback(() => {
    if (shouldAllowBulkActions) selectAll();
  }, [selectAll, shouldAllowBulkActions]);

  const updateSelectedNode = useCallback(
    (node: DataRoomNode) => {
      if (isViewer) return null;

      updateSelected(node);
    },
    [isViewer, updateSelected],
  );

  const onSelectNode = useCallback(
    (selectedNode: DataRoomNode) => {
      if (!shouldAllowBulkActions) return null;

      onSelect(selectedNode);
    },
    [shouldAllowBulkActions, onSelect],
  );

  useEffect(() => {
    removeAll();
  }, [pathname, removeAll]);

  return (
    <DataRoomSelectedDocumentsContext.Provider
      value={{
        selected,
        isAllSelected,
        isSingleSelected,
        selectedLabel,
        shouldRename,
        shouldNotManage,
        shouldAllowBulkActions,
        isSingleSelectedFolder,
        isSingleSelectedVideo,
        canDownloadSelected,
        isSelectedMode,
        canShareFiles,
        hasAuthorizedActionsForAllSelected: hasAuthorizedActionsForAll,
        setSelected,
        removeAll,
        onSelect: onSelectNode,
        updateSelected: updateSelectedNode,
        updateSelectedValue: updateSelectedValue,
        selectAll: selectAllNodes,
      }}
    >
      {children}
    </DataRoomSelectedDocumentsContext.Provider>
  );
};

export default DataRoomDocumentsProvider;
